<template>
	<div class="search-edit-block">
		<default-widget-field v-bind="defaultFieldProps" v-on="defaultFieldListeners" />
		<div class="active-mode">
			<v-autocomplete
				filled
				return-object
				class="widget-search-field-dropdown widget-input-field"
				v-model="value"
				outlined
				ref="searchInput"
				item-value="value"
				item-text="option"
				:menu-props="{ contentClass: 'search-edit-block-menu' }"
				:rules="fieldRules"
				@focus="initSearch"
				:items="totalAcItems"
				:loading="loadingItems"
				:search-input.sync="search"
				:chips="isMultipleSelection"
				:multiple="isMultipleSelection"
				:placeholder="placeholderValueLocal"
				:disabled="!isServiceAndActionSelected"
			>
				<template #item="{ item }">
					<SearchFieldListItem :item="item" :isMultipleSelection="isMultipleSelection" :value="itemValue(item)" />
				</template>

				<template #selection="data">
					<SearchFieldSelectedItem :data="data" :isMultipleSelection="isMultipleSelection" />
				</template>

				<template #append-item v-if="hasMoreData">
					<InsertDataLoadMore
						@load-more="loadMore"
						:isLoading="loadingItems"
						:isFiltered="isFiltered"
						:hasMoreData="hasMoreData"
						:totalItemsLength="totalAcItems.length"
						:totalItemsOnDisplay="filteredACItems.length"
					/>
				</template>

				<template #no-data v-if="errorMessage">
					<v-list-item>
						<span class="error-message">
							{{ errorMessage }}
						</span>
					</v-list-item>
				</template>
			</v-autocomplete>
		</div>
	</div>
</template>

<script>
/*
	Friendly reminder: ** DO NOT IMPORT STORES **
	this runs as widgets live for users, at this point we do not have all the data in the stores.

	- If you do, make sure you have a way to determine this is an interaction within Flowbuilder and not during widget run
*/

import uniqBy from 'lodash/uniqBy';
import { mapGetters } from 'vuex';

import SearchFieldMixin from './SearchFieldMixin';
import WidgetFormService from '../../../WidgetForm/WidgetFormService';
import DefaultWidgetField from '../DefaultWidgetField';
import WidgetFieldMixin from '../WidgetFieldMixin';
import api from '@/api';
import pagination from '@/api/pagination';
import WidgetFieldService from '@/components/BlockEditorWidget/WidgetFieldService';
import InsertDataLoadMore from '@/components/DataInput/DataInputMenu/MenuLists/InsertDataLoadMore';
import { getAction } from '@/store/modules/actions/getters';
import { configurationsValuesByFieldUUID } from '@/store/modules/configurationEditor/getters';
import CredentialsService from '@/utils/credentials-service';
import { pluralize } from '@/utils/language-service';

export default {
	name: 'SearchField',
	components: {
		DefaultWidgetField,
		InsertDataLoadMore,
	},
	mixins: [WidgetFieldMixin, SearchFieldMixin],
	props: {
		previousSteps: { type: Object },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
		configurationId: { type: Number },
	},
	computed: {
		...mapGetters([getAction.fullPath, configurationsValuesByFieldUUID.fullPath]),
		configurationEditingMode() {
			return !!this.configurationId;
		},
		credentialsFromConfiguration() {
			if (!this.configurationEditingMode) {
				return; // No way.
			}
			const sourceUUID = this.field.metadata.source.credentials_configuration.source_field_uuid;
			return this[configurationsValuesByFieldUUID.fullPath][sourceUUID];
		},
		isFiltered() {
			return Boolean(!this.isSearchable && this.search && this.search.length > 0);
		},
		filteredACItems() {
			return this.totalAcItems.filter((item) => item.option?.toLowerCase().includes(this.search?.toLowerCase()));
		},
		actionId() {
			return this.field.metadata.source?.action;
		},
		actionData() {
			return this[getAction.fullPath](this.actionId);
		},
		isServiceAndActionSelected() {
			return this.field.metadata.source.service && this.field.metadata.source.action;
		},
		isConnectionFromConfiguration() {
			return CredentialsService.isConnectionFromConfiguration(this.field.metadata.source.credentials_configuration.credentials_type);
		},
		areCredentialsFieldsSet() {
			if (this.isConnectionFromConfiguration) {
				const credsSourceFieldUUID = this.field.metadata.source.credentials_configuration.source_field_uuid;
				if (this.configurationEditingMode) {
					return !!credsSourceFieldUUID && !!this.credentialsFromConfiguration;
				} else {
					return !!credsSourceFieldUUID;
				}
			} else {
				return Boolean(this.field.metadata.source?.credentials);
			}
		},
		isMapperNeededAndSet() {
			if (this.actionData?.has_user_schema && this.isConnectionFromConfiguration) {
				return Boolean(this.field.metadata.source.user_schema_mappings_source_field_uuid);
			}
			return true;
		},

		value: {
			/* Custome value getter and setter */
			get() {
				if (Array.isArray(this.fieldValue)) {
					const values = this.fieldValue || [];
					return this.totalAcItems.filter((item) => values.includes(item.value));
				} else {
					return this.acItems.find((item) => item.value === this.fieldValue);
				}
			},
			set(value) {
				let fieldValue;
				let fieldLabel;
				this.preventNextSearchCall = true;
				if (Array.isArray(value)) {
					fieldValue = value.map((v) => v.value);
					fieldLabel = value.map((v) => v.option);
					this.saveValueToChosenAcItems(value);
				} else if (value) {
					fieldValue = value.value;
					fieldLabel = value.option;
				} else {
					fieldValue = this.isMultipleSelection ? [] : '';
					fieldLabel = '';
				}
				this.setFieldValue(fieldValue, fieldLabel);
			},
		},
		placeholderValueLocal: {
			set(value) {
				this.$emit('update:placeholderValue', value);
			},
			get() {
				if (!this.placeholderEnabled) {
					return '';
				}
				return this.placeholderValue;
			},
		},
		isMultipleSelection() {
			return WidgetFieldService.isMultipleSelection(this.field);
		},
		fieldRules() {
			return [];
		},
		fieldSourceURL() {
			return this.field.source_url;
		},
		totalAcItems() {
			const totalAcItems = [...this.acItems, ...this.chosenAcItems];
			if (!this.search && totalAcItems.length === 0 && !this.errorMessage) {
				return [{ header: 'Type to start searching...' }];
			}
			return uniqBy(totalAcItems, 'value'); // distinct items
		},
		isAcItemsEmpty() {
			return this.totalAcItems.length === 0 || (this.totalAcItems.length === 1 && !!this.totalAcItems[0].header);
		},
		noun() {
			return this.fieldOptions.noun ? pluralize(this.fieldOptions.noun) : pluralize('item');
		},
		customDescriptionSettings() {
			return this.fieldOptions.custom?.description?.enabled ? this.fieldOptions.custom.description.value : undefined;
		},
		customImageSettings() {
			return this.fieldOptions.custom?.image?.enabled ? this.fieldOptions.custom.image.value : undefined;
		},
		prefillSettings() {
			return this.fieldOptions.searchPrefill;
		},
		prefillEnabled() {
			return this.prefillSettings?.enabled || false;
		},
		prefillPreselectEnabled() {
			return this.prefillSettings.preselect?.enabled || false;
		},
		prefillValue() {
			if (this.previousSteps && this.prefillSettings.value) {
				const prevValue = WidgetFormService.processPreviousSteps(
					this.prefillSettings.value,
					this.previousSteps,
					this.replaceInsertDataStringDict,
					this.configurationsMappingBlocksUUIDs
				);
				return prevValue == 'undefined' ? this.prefillSettings.value : prevValue;
			}
			return '';
		},
		customAcMappings() {
			const mappings = {};
			if (this.customDescriptionSettings) {
				mappings.ply_custom_description = this.customDescriptionSettings;
			}
			if (this.customImageSettings) {
				mappings.ply_custom_image = this.customImageSettings;
			}
			return mappings;
		},
	},
	methods: {
		focus() {
			// THIS IS THE ONLY WIDGET FIELD THAT HAS THAT FUNC EXPLICITLY, CAUSE HE's THE ONLY ONE NOT SHARING THE WidgetFieldMixin file.
			this.$refs?.searchInput?.focus?.();
		},
		async loadMenuItems() {
			if (!this.areCredentialsFieldsSet || !this.isMapperNeededAndSet || !this.fieldSourceURL) {
				this.errorMessage = 'Please finish setting up search field';
				return;
			}
			if (this.loadingItems) {
				return;
			}
			this.loadingItems = true;
			this.hasMoreData = true;
			this.errorMessage = null;
			// Guy Removed this: (28.05)
			// this.preventNextSearchCall = true;
			const nodeRunId = await this.handleNodeRunIdPromise();
			const overrideCredentials = this.credentialsFromConfiguration || null;
			const res = await api.queryAC(
				this.fieldSourceURL,
				this.search,
				this.page,
				this.retrievalId,
				false,
				this.customAcMappings,
				nodeRunId,
				overrideCredentials
			);
			this.loadingItems = false;
			const response = pagination.consolidateQueryACResponse(res);
			if (response.errorMessage) {
				this.hasMoreData = false;
				this.errorMessage = response.errorMessage;
				this.$set(this, 'acItems', []);
				if (!this.isMultipleSelection) {
					this.value = { value: '', option: '' };
				}
				return;
			}

			this.hasMoreData = response.hasMoreData;
			this.retrievalId = response.retrievalId;
			if (this.page === 1) {
				this.$set(this, 'acItems', response.results || []);
			} else {
				this.acItems.push(...response.results);
			}

			if (
				// prefill value if only one
				this.prefillEnabled &&
				this.prefillPreselectEnabled &&
				this.search
			) {
				const maybeOneValue = response.results.filter((x) => x.option.toLowerCase().includes(this.search.toLowerCase()));
				if (maybeOneValue.length === 1) {
					const value = this.isMultipleSelection ? [maybeOneValue[0]] : maybeOneValue[0];
					this.value = value;
				}
			}
		},
		saveValueToChosenAcItems(values) {
			this.$set(this, 'chosenAcItems', values);
		},
		itemValue(item) {
			if (this.isMultipleSelection) {
				return this.value?.find((x) => x.value === item.value);
			}
			return {};
		},
		initSearch() {
			if (this.prefillValue && !this.search) {
				//"touch" search so we will call watch - only after rendered properly (instead of using "immediate")
				this.search = this.prefillValue;
			}
		},
		autoLoadDataIfNeeded() {
			if (!this.isSearchable) {
				this.loadMenuItems();
			}
		},
		async handleNodeRunIdPromise() {
			return this.field.nodeRunIdPromise;
		},
	},
	mounted() {
		this.autoLoadDataIfNeeded();
		this.preventNextSearchCall = false;
	},
	watch: {
		actionData(newV) {
			this.isSearchable = newV?.searchable !== false; //if it's undefined,the default is true
		},
		isServiceAndActionSelected() {
			console.log(`calling loadingItems from isServiceAndActionSelected`);
			this.autoLoadDataIfNeeded();
		},
		areCredentialsFieldsSet() {
			console.log(`calling loadingItems from areCredentialsFieldsSet`);
			this.loadMenuItems();
		},
	},
	data() {
		return {
			page: 1,
			acItems: [],
			search: null,
			chosenAcItems: [],
			retrievalId: null,
			errorMessage: null,
			hasMoreData: false,
			loadingItems: false,
			isSearchable: false,
			preventNextSearchCall: false,
			needToChangeSearch: true,
		};
	},
};
</script>

<style scoped lang="scss">
.search-edit-block {
	.active-mode {
		::v-deep(.v-input__slot) {
			background-color: transparent !important;
			padding-right: 4px !important;
		}
	}
	::v-deep(.v-input__append-inner) {
		margin-top: 5px !important;
	}
}
.error-message {
	color: #ff5252;
}
.search-count {
	font-size: small;
	opacity: 0.5;
}
.load-more-btn {
	width: 100%;
}
</style>

<style lang="scss">
// Warning: This is set on the global scope which is generally bad so look out for CSS collisions
// and keep wrapped with a singular class name
.search-edit-block-menu {
	.v-list-item {
		padding: 0 11px;
		min-height: 30px;
	}
}
</style>
